import React, { useCallback, useEffect, useRef, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { canUseBillingSync } from "../../../helpers/billingSyncEnabled";
import { checkMemoInZoho, getZohoStatus, pushMemoToZoho, voidMemoZohoDraft } from "../../../services/billingSyncApi";
import SalesSheet from "./SalesSheet";

type VoidThen = "reopen" | "cancel" | "";

/** How long to wait before each look at a push running elsewhere; after the last, the screen stops looking. */
const PUSH_LOOK_DELAYS = [5000, 15000, 60000];

export type ZohoMemo = {
  /** Zoho is switched on, connected, and this is a Ready sale memo: Zoho owns the next step. */
  on: boolean;
  /** Still asking whether Zoho is connected: no billing step may be offered yet. */
  checking: boolean;
  /** A Zoho draft holds this memo. */
  hasDraft: boolean;
  /** A push is running and has no draft number yet. */
  pushing: boolean;
  /** Zoho holds this memo (a draft, or a push running): never close, edit or cancel it by hand while true. */
  held: boolean;
  /** HKP cannot reach the Zoho organisation holding the draft: the admin voids it in Zoho and confirms here. */
  byHand: boolean;
  /** Only the studio admin may confirm a draft voided by hand. */
  canConfirmByHand: boolean;
  /** The Zoho connection could not be read (network or server error): nothing is assumed; offer a retry. */
  statusFailed: boolean;
  retryStatus: () => void;
  busy: boolean;
  push: () => Promise<void>;
  check: () => Promise<void>;
  voidFor: VoidThen | null;
  openVoid: (then: VoidThen) => void;
  closeVoid: () => void;
  voidDraft: (then: VoidThen, reason?: string, byHand?: { handReason: string }) => Promise<void>;
};

/**
 * Zoho Books on a Ready sale memo. The memo stays the source of truth: after every Zoho action the memo is
 * read again, so the screen always shows what the server now holds.
 */
export const useZohoMemo = ({ memo, role, active, reload, onCancelled }: {
  memo: any;
  role?: string;
  active: boolean;
  reload: () => Promise<void>;
  onCancelled: () => void;
}): ZohoMemo => {
  const allowed = active && canUseBillingSync(role);
  // null: still reading; "failed": the read itself failed, which says nothing about the connection.
  const [connected, setConnected] = useState<boolean | null | "failed">(null);
  const [statusRead, setStatusRead] = useState(0);
  const [busy, setBusy] = useState(false);
  const [voidFor, setVoidFor] = useState<VoidThen | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const memoId = memo?.id || "";
  const hasDraft = !!memo?.zohoInvoiceNumber;
  const pushing = !hasDraft && !!memo?.zohoPushing;
  const checkedOnOpen = useRef("");

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    setConnected(null);
    getZohoStatus()
      .then((res: any) => live && setConnected(res?.success ? !!res?.data?.connected : "failed"))
      .catch(() => live && setConnected("failed"));
    return () => { live = false; };
  }, [allowed, statusRead]);

  const run = useCallback(async (title: string, call: () => Promise<any>, done: (res: any) => void,
    refused?: (res: any) => boolean) => {
    if (busy || !memoId) return;
    setBusy(true);
    try {
      const res: any = await call();
      if (!res?.success) {
        if (refused && refused(res)) return;
        notifications({ title, message: res?.message || "Try again", type: "danger" });
        return;
      }
      done(res);
    } catch {
      notifications({ title, message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
    }
  }, [busy, memoId]);

  const push = useCallback(() => run("Not sent to Zoho", () => pushMemoToZoho(memoId), async (res) => {
    notifications({ title: "Draft in Zoho", message: `${res?.data?.zohoInvoiceNumber || "The draft"} is waiting for your accountant.`, type: "success" });
    await reload();
  }), [memoId, reload, run]);

  const check = useCallback(() => run("Could not check Zoho", () => checkMemoInZoho(memoId), async (res) => {
    const data = res?.data || {};
    if (data.status === "INVOICE_GENERATED") {
      const added: string[] = Array.isArray(data.addedFromZoho) ? data.addedFromZoho : [];
      // Sarees the accountant typed into the Zoho invoice by hand joined the memo and moved with it.
      notifications({ title: "Billed", message: `Invoice ${data.invoiceNumber || data.zohoInvoiceNumber} is approved in Zoho.`
        + (added.length ? ` Added from the Zoho invoice: ${added.join(", ")}.` : ""), type: "success" });
    } else if (data.zohoStatus === "void" || data.zohoStatus === "deleted") {
      notifications({ title: "Draft gone from Zoho", message: "It was voided or deleted there. Create it again when ready.", type: "warning" });
    } else {
      notifications({ title: "Still a draft", message: "Your accountant has not approved it in Zoho yet.", type: "warning" });
    }
    await reload();
  }), [memoId, reload, run]);

  const voidDraft = useCallback((then: VoidThen, reason?: string, byHand?: { handReason: string }) => run("Zoho draft not voided",
    () => voidMemoZohoDraft(memoId, then || undefined, reason, byHand), async () => {
      setVoidFor(null);
      setUnreachable(false);
      notifications({ title: "Zoho draft voided", message: then === "cancel" ? "The memo is cancelled." : then === "reopen"
        ? "You can edit the memo now." : "Create it again when ready.", type: "success" });
      if (then === "cancel") {
        onCancelled();
        return;
      }
      await reload();
    }, (res) => {
      // HKP cannot reach that Zoho organisation: the sheet switches to "void it in Zoho, then confirm here".
      if (res?.code !== "ZOHO_VOID_BY_HAND") return false;
      setUnreachable(true);
      return true;
    }), [memoId, onCancelled, reload, run]);

  // A push running elsewhere has no draft number yet: look again a few times, further apart, then stop.
  useEffect(() => {
    if (!pushing) return;
    let look = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const next = () => {
      const delay = PUSH_LOOK_DELAYS[look];
      if (delay === undefined) return;
      timer = setTimeout(() => { look += 1; reload(); next(); }, delay);
    };
    next();
    return () => clearTimeout(timer);
  }, [pushing, reload]);

  // Opening a memo whose draft is waiting (or already approved, but not yet closed) checks Zoho once; the server
  // skips Zoho if it checked under a minute ago. Any change reloads the memo, so the screen never offers a dead step.
  useEffect(() => {
    if (!allowed || connected !== true || !hasDraft || checkedOnOpen.current === memoId) return;
    checkedOnOpen.current = memoId;
    const seen = { status: memo?.status, zohoStatus: memo?.zohoStatus, zohoInvoiceNumber: memo?.zohoInvoiceNumber };
    checkMemoInZoho(memoId)
      .then((res: any) => {
        const data = res?.data;
        if (!res?.success || !data) return;
        if (data.status !== seen.status || data.zohoStatus !== seen.zohoStatus
          || (data.zohoInvoiceNumber || null) !== (seen.zohoInvoiceNumber || null)) reload();
      })
      .catch(() => undefined);
  }, [allowed, connected, hasDraft, memo?.status, memo?.zohoStatus, memo?.zohoInvoiceNumber, memoId, reload]);

  return {
    on: allowed && connected === true,
    checking: allowed && connected === null,
    hasDraft,
    pushing,
    held: hasDraft || pushing,
    byHand: hasDraft && (unreachable || !allowed || connected === false),
    canConfirmByHand: role === "ORGANIZATION_ADMIN",
    statusFailed: allowed && connected === "failed",
    retryStatus: () => setStatusRead((n) => n + 1),
    busy,
    push,
    check,
    voidFor,
    openVoid: setVoidFor,
    closeVoid: () => { setVoidFor(null); setUnreachable(false); },
    voidDraft,
  };
};

const approvedInZoho = (memo: any) => !!memo?.zohoStatus && memo.zohoStatus !== "draft";

/** The memo's "Next" note while Zoho owns the next step; null leaves the usual note. */
export const zohoNextStep = (memo: any, zoho: ZohoMemo): string | null => {
  if (zoho.pushing) return "the draft invoice is being created in Zoho Books. This screen updates in a moment.";
  if (zoho.statusFailed) return "HKP could not check the Zoho Books connection just now. Try again before billing this memo.";
  if (zoho.hasDraft && zoho.byHand) {
    return "Zoho Books is not connected for this draft. Connect it again in Settings → Zoho Books, or void the invoice in Zoho yourself and confirm under Void Zoho draft.";
  }
  if (!zoho.on) return null;
  if (!zoho.hasDraft) return "create the draft invoice in Zoho Books. Your accountant checks and approves it there. Pieces stay reserved for this party until then.";
  return approvedInZoho(memo)
    ? "your accountant has approved it in Zoho. Tap Check Zoho: the memo closes with Zoho's invoice number."
    : "your accountant approves this draft in Zoho Books. Then tap Check Zoho: the memo closes with Zoho's invoice number.";
};

/** One line under the memo's "Next" note: where the bill stands in Zoho. */
export const ZohoMemoStatus = ({ memo, zoho }: { memo: any; zoho: ZohoMemo }) => {
  if (zoho.pushing) {
    return <div className="sp-zoho" aria-live="polite"><span className="sp-st is-draft">Sending to Zoho…</span></div>;
  }
  if (zoho.statusFailed) {
    return (
      <div className="sp-zoho" role="alert">
        <span className="sp-st is-closed">Zoho not checked</span>
        <button type="button" className="sp-link" onClick={zoho.retryStatus}>Try again</button>
      </div>
    );
  }
  if (zoho.hasDraft) {
    return (
      <div className="sp-zoho" aria-live="polite">
        <span className="sp-st is-draft">{approvedInZoho(memo) ? "Approved in Zoho" : "Draft in Zoho"}</span>
        <span className="mono sp-zoho__no">{memo.zohoInvoiceNumber}</span>
      </div>
    );
  }
  if (zoho.on && (memo?.zohoStatus === "void" || memo?.zohoStatus === "deleted")) {
    return (
      <div className="sp-zoho">
        <span className="sp-st is-closed">Zoho draft {memo.zohoStatus === "void" ? "voided" : "deleted"}</span>
      </div>
    );
  }
  return null;
};

/** Void the Zoho draft (never delete: it already holds an invoice number), then edit, cancel, or leave the memo. */
export const ZohoVoidSheet = ({ memo, zoho }: { memo: any; zoho: ZohoMemo }) => {
  const [then, setThen] = useState<VoidThen>(zoho.voidFor || "");
  const [reason, setReason] = useState("");
  const [doneInZoho, setDoneInZoho] = useState(false);
  const [handReason, setHandReason] = useState("");
  useEffect(() => setThen(zoho.voidFor || ""), [zoho.voidFor]);
  useEffect(() => { setDoneInZoho(false); setHandReason(""); }, [zoho.voidFor, zoho.byHand]);
  const byHandReady = zoho.canConfirmByHand && doneInZoho && !!handReason.trim();
  if (zoho.voidFor === null) return null;
  const choices: { value: VoidThen; label: string; hint: string }[] = [
    { value: "reopen", label: "Void and edit the memo", hint: "Change pieces or prices, then create a new draft." },
    { value: "cancel", label: "Void and cancel the memo", hint: "Its pieces go back to stock." },
    { value: "", label: "Only void the draft", hint: "The memo stays Ready; create the draft again later." },
  ];
  return (
    <SalesSheet label="Void Zoho draft" onClose={zoho.closeVoid}>
      <div className="sp-zoho-void">
        <h3 className="t21">Void {memo?.zohoInvoiceNumber || "the Zoho draft"}?</h3>
        {zoho.byHand ? (
          <p className="t13" role="status">
            HKP cannot reach the Zoho organisation that holds this invoice. Open Zoho Books, void it there (never delete
            it), then confirm here. Or connect that organisation again in Settings → Zoho Books.
          </p>
        ) : (
          <p className="t13">Zoho keeps the number as a void invoice, so the series has no gap. This cannot be undone.</p>
        )}
        <div className="sp-zoho-void__choices" role="radiogroup" aria-label="After voiding">
          {choices.map((choice) => (
            <label key={choice.value || "only"} className={`sp-zoho-void__choice${then === choice.value ? " is-on" : ""}`}>
              <input type="radio" name="zoho-void-then" checked={then === choice.value} onChange={() => setThen(choice.value)} />
              <span><b>{choice.label}</b><span className="t13">{choice.hint}</span></span>
            </label>
          ))}
        </div>
        {then === "cancel" ? (
          <label className="sp-zoho-void__reason">
            <span className="t13">Reason (optional)</span>
            <input value={reason} maxLength={140} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Party changed its mind" />
          </label>
        ) : null}
        {zoho.byHand && zoho.canConfirmByHand ? (
          <>
            <label className="sp-zoho-void__reason sp-zoho-void__check">
              <input type="checkbox" checked={doneInZoho} onChange={(e) => setDoneInZoho(e.target.checked)} />
              <span className="t13">I voided {memo?.zohoInvoiceNumber || "this invoice"} in Zoho Books</span>
            </label>
            <label className="sp-zoho-void__reason">
              <span className="t13">How it was voided (kept on the memo)</span>
              <input value={handReason} maxLength={200} onChange={(e) => setHandReason(e.target.value)}
                placeholder="e.g. Voided in Zoho by the accountant" />
            </label>
          </>
        ) : null}
        {zoho.byHand && !zoho.canConfirmByHand ? (
          <p className="t13 sp-warn">Only your studio admin can confirm a draft voided by hand. Ask them to open this memo.</p>
        ) : null}
        <div className="sp-btns">
          <button type="button" className="sp-btn" onClick={zoho.closeVoid} disabled={zoho.busy}>Keep the draft</button>
          <button type="button" className="sp-btn is-danger" disabled={zoho.busy || (zoho.byHand && !byHandReady)}
            onClick={() => zoho.voidDraft(then, then === "cancel" ? reason.trim() || undefined : undefined,
              zoho.byHand ? { handReason: handReason.trim() } : undefined)}>
            {zoho.busy ? "Voiding…" : zoho.byHand ? "Confirm voided" : "Void draft"}
          </button>
        </div>
      </div>
    </SalesSheet>
  );
};
